import { useState } from "react";
import { ArrowRight, ChevronDown, Search } from "zudoku/icons";
import { useLocation } from "zudoku/router";
import { Popover, PopoverContent, PopoverTrigger } from "zudoku/ui/Popover.js";
import { products } from "./products.js";
import "./product-navigation.css";

const categories = [...new Set(products.map((product) => product.category))];

const ProductMenu = ({
  label,
  initialCategory = "全部产品",
  active = false,
}: {
  label: string;
  initialCategory?: string;
  active?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(initialCategory);
  const [query, setQuery] = useState("");
  const matches = products.filter(
    (product) =>
      (category === "全部产品" || product.category === category) &&
      [product.name, product.description, ...product.tags]
        .join(" ")
        .includes(query.trim()),
  );
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setCategory(initialCategory);
          setQuery("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`ax-nav-trigger${active ? " is-current" : ""}`}
        >
          {label === "全部产品" && <Search size={16} />}
          {label}
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={12}
        className="ax-product-menu"
        aria-label={`${label}产品选择`}
      >
        <div className="ax-menu-search">
          <Search size={18} />
          <input
            autoComplete="off"
            aria-label="搜索导航产品"
            placeholder="搜索产品名称或功能"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button type="button" onClick={() => setQuery("")}>
              清除
            </button>
          )}
        </div>
        <div className="ax-menu-body">
          <div
            className="ax-menu-categories"
            role="group"
            aria-label="产品业务分类"
          >
            {["全部产品", ...categories].map((item) => (
              <button
                type="button"
                key={item}
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="ax-menu-results">
            <p className="ax-menu-label" role="status">
              {category} · {matches.length} 款产品
            </p>
            <div className="ax-menu-grid">
              {matches.map((product) => (
                <a
                  key={product.id}
                  href={`/${product.id}/features`}
                  onClick={() => setOpen(false)}
                >
                  <strong>
                    {product.name}
                    <ArrowRight size={14} />
                  </strong>
                  <span>{product.tags.join(" · ")}</span>
                </a>
              ))}
            </div>
            {matches.length === 0 && (
              <p className="ax-menu-empty">
                没有找到相关产品，请更换关键词或分类。
              </p>
            )}
          </div>
        </div>
        <div className="ax-menu-footer">
          <span>按产品查阅演示、功能与技术文档</span>
          <a href="/overview">
            进入文档中心 <ArrowRight size={14} />
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ProductNavigation = ({ home = false }: { home?: boolean }) => {
  const { pathname } = useLocation();
  const current = products.find((product) =>
    pathname.startsWith(`/${product.id}/`),
  );
  return (
    <div
      className={`ax-product-navigation${home ? " ax-nav-home" : " ax-nav-docs"}`}
    >
      <nav aria-label="产品导航">
        <a
          href={home ? "/" : "/overview"}
          aria-current={
            pathname === (home ? "/" : "/overview") ? "page" : undefined
          }
        >
          {home ? "首页" : "文档总览"}
        </a>
        <ProductMenu
          label={home ? "产品与服务" : "切换产品"}
          active={Boolean(current)}
        />
        {home && (
          <a
            href="/overview"
            aria-current={pathname === "/overview" ? "page" : undefined}
          >
            文档中心
          </a>
        )}
      </nav>
      {!home && current && (
        <span className="ax-current-product">
          当前产品 <strong>{current.name}</strong>
        </span>
      )}
    </div>
  );
};
